import { SizeMap } from '@/themes';
import {
  Components,
  Theme,
  CSSObject,
  alpha,
  ThemeOptions,
} from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────
// TYPE DEFINITIONS
// ─────────────────────────────────────────────────────────────

type ButtonVariantStyle = CSSObject;

const getButtonTheme = (theme: Theme): ThemeOptions => {
  // ─────────────────────────────────────────────────────────────
  // STYLE CONSTANTS
  // ─────────────────────────────────────────────────────────────

  // Button Size Map
  // Example: size: { button: { s: { fontSize: '14px', width: '14px', height: '14px' }, ... } }
  const buttonSize: SizeMap = {
    s: { fontSize: '14px', width: '14px', height: '14px' },
    m: { fontSize: '18px', width: '18px', height: '18px' },
    l: { fontSize: '18px', width: '18px', height: '18px' },
    xl: { fontSize: '22px', width: '22px', height: '22px' },
  };

  // Primary Filled Button Styles
  const filledPrimaryStyles: ButtonVariantStyle = {
    backgroundColor: theme.palette.M3.sys.light.primary,
    color: theme.palette.M3.sys.light['on-primary'],
    '&:hover': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.08'],
    },
    '&:focus::after, &:active::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      borderRadius: 'inherit',
    },
    '&:focus::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.16'],
    },
    '&:active::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.12'],
    },
    '&.Mui-disabled': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Destructive Filled Button Styles
  const filledDestructiveStyles: ButtonVariantStyle = {
    backgroundColor: theme.palette.State.error,
    color: theme.palette.State['on-error'],
    '&:hover': {
      backgroundColor: theme.palette.M3.sys.light['on-error-container'],
    },
    '&:focus::after, &:active::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      borderRadius: 'inherit',
    },
    '&:focus::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.16'],
    },
    '&:active::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.12'],
    },
    '&.Mui-disabled': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
      colorOpacity: 0.38,
    },
  };

  // Primary Outlined Button Styles
  const outlinedPrimaryStyles: ButtonVariantStyle = {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.M3.sys.light.primary}`,
    color: theme.palette.M3.sys.light.primary,
    '&:hover, &:focus , &:active': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.08'],
      color: theme.palette.M3.sys.light['on-primary'],
      borderColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.08'],
    },
    '&.Mui-disabled': {
      borderColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Destructive Outlined Button Styles
  const outlinedDestructiveStyles: ButtonVariantStyle = {
    borderColor: theme.palette.M3.sys.light.outline,
    color: theme.palette.State.error,
    '&:hover': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-fixed'][
          'opacity-0.08'
        ],
    },
    '&:focus': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-container'][
          'opacity-0.12'
        ],
      borderColor: theme.palette.State.error,
    },
    '&:active': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-container'][
          'opacity-0.12'
        ],
      borderColor: theme.palette.State.error,
    },
    '&.Mui-disabled': {
      borderColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Primary Text Button Styles
  const textPrimaryStyles: ButtonVariantStyle = {
    backgroundColor: 'transparent',
    color: theme.palette.M3.sys.light.primary,
    '&:hover': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['primary']['opacity-0.08'],
    },
    '&:focus': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['primary']['opacity-0.12'],
    },
    '&:active': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['primary']['opacity-0.12'],
    },
    '&.Mui-disabled': {
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Destructive Text Button Styles
  const textDestructiveStyles: ButtonVariantStyle = {
    color: theme.palette.State.error,
    '&:hover': { backgroundColor: theme.palette.State['error-background'] },
    '&:focus': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-container'][
          'opacity-0.12'
        ],
    },
    '&:active': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-container'][
          'opacity-0.12'
        ],
    },
    '&.Mui-disabled': {
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Primary Tonal Button Styles
  const tonalPrimaryStyles: ButtonVariantStyle = {
    backgroundColor: theme.palette.M3.sys.light['secondary-container'],
    color: theme.palette.M3.sys.light['on-secondary-container'],
    '&:hover::after, &:focus::after, &:active::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      borderRadius: 'inherit',
    },
    '&:hover::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-secondary-container'][
          'opacity-0.08'
        ],
    },
    '&:focus::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-secondary-container'][
          'opacity-0.12'
        ],
    },
    '&:active::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-secondary-container'][
          'opacity-0.12'
        ],
    },
    '&.Mui-disabled': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // ─────────────────────────────────────────────────────────────
  // THEME EXPORT
  // ─────────────────────────────────────────────────────────────

  const buttonTheme: Components<Theme>['MuiButton'] = {
    defaultProps: {
      size: 's',
      fullWidth: false,
      disableElevation: true,
      disableRipple: true,
    },

    styleOverrides: {
      root: {
        borderRadius: theme.unit(25),
        textTransform: 'none',
        width: 'fit-content',
        boxSizing: 'border-box',
        ...theme.typography.M3.label.mediumProminent,
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
          margin: 0,
        },
      },
    },

    variants: [
      // ─────────────────────────────
      // SIZE VARIANTS (gap + padding + icon size)
      // ─────────────────────────────
      {
        props: { size: 's' },
        style: {
          ...theme.typography.M3.label.mediumProminent,
          gap: theme.unit(1),
          padding: theme.unit(1.5, 2.5),
          borderRadius: theme.unit(1.5),
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            '& > svg': {
              ...buttonSize.s, // Spread the size config
            },
          },
        },
      },
      {
        props: { size: 'm' },
        style: {
          ...theme.typography.M3.label.large,
          gap: theme.unit(2),
          padding: theme.unit(2, 4),
          borderRadius: theme.unit(2.5),
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            '& > svg': {
              ...buttonSize.m, // Spread the size config
            },
          },
        },
      },
      {
        props: { size: 'l' },
        style: {
          ...theme.typography.M3.label.large,
          gap: theme.unit(2),
          padding: theme.unit(2.5, 6),
          borderRadius: theme.unit(3),
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            '& > svg': {
              ...buttonSize.l, // Spread the size config
            },
          },
        },
      },
      {
        props: { size: 'xl' },
        style: {
          ...theme.typography.M3.title.medium,
          gap: theme.unit(2),
          padding: theme.unit(2.5, 7),
          borderRadius: theme.unit(3.5),
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            '& > svg': {
              ...buttonSize.xl, // Spread the size config
            },
          },
        },
      },

      // ─────────────────────────────
      // FILLED BUTTONS
      // ─────────────────────────────
      {
        props: { variant: 'filled' },
        style: filledPrimaryStyles,
      },
      {
        props: { variant: 'filled', color: 'primary' },
        style: filledPrimaryStyles,
      },

      // ─────────────────────────────
      // OUTLINED BUTTONS
      // ─────────────────────────────
      {
        props: { variant: 'outlined' },
        style: outlinedPrimaryStyles,
      },
      {
        props: { variant: 'outlined', color: 'primary' },
        style: outlinedPrimaryStyles,
      },

      // ─────────────────────────────
      // TEXT BUTTONS
      // ─────────────────────────────
      {
        props: { variant: 'text' },
        style: textPrimaryStyles,
      },
      {
        props: { variant: 'text', color: 'primary' },
        style: textPrimaryStyles,
      },

      // ─────────────────────────────
      // TONAL BUTTONS (Surface Variant)
      // ─────────────────────────────
      {
        props: { variant: 'tonal' },
        style: tonalPrimaryStyles,
      },
      {
        props: { variant: 'tonal', color: 'primary' },
        style: tonalPrimaryStyles,
      },

      // ─────────────────────────────
      // DESTRUCTIVE COLOR - FILLED
      // ─────────────────────────────
      {
        props: { variant: 'filled', color: 'destructive' },
        style: filledDestructiveStyles,
      },

      // ─────────────────────────────
      // DESTRUCTIVE COLOR - OUTLINED
      // ─────────────────────────────
      {
        props: { variant: 'outlined', color: 'destructive' },
        style: outlinedDestructiveStyles,
      },

      // ─────────────────────────────
      // DESTRUCTIVE COLOR - TEXT
      // ─────────────────────────────
      {
        props: { variant: 'text', color: 'destructive' },
        style: textDestructiveStyles,
      },

      // ─────────────────────────────
      // FULL WIDTH
      // ─────────────────────────────
      {
        props: { fullWidth: true },
        style: { width: '100%' },
      },
    ],
  };

  return {
    components: { MuiButton: buttonTheme },
    size: { button: buttonSize },
  };
};

export default getButtonTheme;
